'use client'

import { useRef } from "react"
import ProductCard from "../product/ProductCard"
import { ArrowUp, Loader2, PackageOpen } from "lucide-react"
import { useInfiniteProducts } from "@/hooks/use-infinite-products"
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll"

export default function AllProducts({ vendor, title, description, session }: { vendor: any, wishlist?: any, title?: string, description?: string, session?: any }) {
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore, pendingCount } = useInfiniteProducts({
    vendorId: vendor?.id,
    limit: 20,
    initialFilters: {},
    session
  })

  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: loadMore,
    enabled: hasMore && !isLoading && !isLoadingMore,
  })

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="cont pt-12 pb-8">
      <div className="flex items-end justify-between gap-4 mb-7">
        <div className="max-w-2xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-2.5" style={{ color: '#a8a8b3' }}>Бүх цуглуулга</div>
          <h2 className="text-2xl sm:text-[34px] font-semibold tracking-[-0.02em] leading-none" style={{ fontFamily: 'Gilroy, Inter, sans-serif', color: 'white' }}>
            {title || 'Бүх бүтээгдэхүүн'}
          </h2>
          {description && (
            <p className="mt-3 text-[14px] leading-6 max-w-lg" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {description}
            </p>
          )}
        </div>
        {!isLoading && (
          <span className="text-[12px] tabular-nums shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }}>{metadata.total} бүтээгдэхүүн</span>
        )}
      </div>
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="aspect-4/5 w-full rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
          ))}
        </div>
      ) : products.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10">
            {products.map((product: any, index: number) => (
              <div key={product.customId || product.id}>
                <ProductCard product={product} vendor={vendor} />
              </div>
            ))}
            {Array.from({ length: pendingCount }).map((_, i) => (
              <div key={`pending-${i}`} ref={i === 0 ? loadMoreRef : undefined}>
                <div className="aspect-4/5 w-full rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }} />
                <div className="mt-3 flex flex-col gap-2">
                  <div className="h-4 w-3/4 rounded" style={{ background: 'rgba(255,255,255,0.04)' }} />
                  <div className="h-4 w-1/2 rounded" style={{ background: 'rgba(255,255,255,0.04)' }} />
                </div>
              </div>
            ))}
          </div>
          <div ref={pendingCount === 0 ? loadMoreRef : null} className="w-full pt-12 flex justify-center">
            {isLoadingMore && (
              <div className="flex items-center gap-2 text-[13px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Ачаалж байна...</span>
              </div>
            )}
            {!hasMore && products.length > 0 && (
              <div className="flex flex-col items-center gap-4">
                <span className="text-[12px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Бүх бүтээгдэхүүн ачаалагдлаа ({metadata.total})</span>
                <button onClick={scrollToTop} className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12px] font-medium" style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.14)' }}>
                  <ArrowUp className="w-3.5 h-3.5" /> Дээш буцах
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-16" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <PackageOpen className="w-8 h-8" />
          <div className="text-[14px]">Бүтээгдэхүүн байхгүй байна</div>
        </div>
      )}
    </div>
  )
}
